import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, MapPin } from "lucide-react";
import { getStateBySlug, slugify } from "@/data/states";
import { getAreasForCity } from "@/data/areas";
import { GeoSections } from "@/components/GeoSections";
import { buildLocationContent, breadcrumbSchema, geoCoords } from "@/data/geo-content";
import { ogImageMeta } from "@/lib/og-image";
import { leafCanonical } from "@/lib/canonical";

export const Route = createFileRoute("/states/$state/$town")({
  loader: ({ params }) => {
    const s = getStateBySlug(params.state);
    if (!s) throw notFound();
    const town = s.cities.find((c) => slugify(c) === params.town);
    if (!town) throw notFound();
    return { state: s, town };
  },
  head: ({ loaderData, match, matches }) => {
    if (!loaderData) return {};
    const { state, town } = loaderData;
    const content = buildLocationContent({
      locationName: town,
      stateName: state.name,
      stateCode: state.code,
      scope: "city",
    });
    const title = `Free Vending Machines in ${town}, ${state.code} | $0 Install`;
    const desc = `FREE vending machines for ${town}, ${state.name} businesses. Zero-cost placement, install, restocking & 24/7 maintenance across ${content.coverageNeighborhoods.slice(0, 3).join(", ")}. We try to respond in 4–6 hrs.`;
    const url = `https://vendingmachinefree.com/states/${state.slug}/${slugify(town)}`;
    const coords = geoCoords(state.code, town);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        ...ogImageMeta(),
      ],
      links: leafCanonical(url, { match, matches }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              name: `Vending Machine Free.com — ${town}, ${state.code}`,
              areaServed: { "@type": "City", name: `${town}, ${state.name}` },
              description: desc,
              url,
              geo: { "@type": "GeoCoordinates", latitude: coords.lat, longitude: coords.lng },
            },
            {
              "@context": "https://schema.org",
              "@type": "Service",
              serviceType: "Free Vending Machine Placement",
              areaServed: { "@type": "City", name: `${town}, ${state.name}` },
              provider: { "@type": "Organization", name: "Vending Machine Free.com", url: "https://vendingmachinefree.com" },
            },
            breadcrumbSchema([
              { name: "Home", url: "https://vendingmachinefree.com/" },
              { name: "USA Coverage", url: "https://vendingmachinefree.com/coverage" },
              { name: state.name, url: `https://vendingmachinefree.com/states/${state.slug}` },
              { name: town, url },
            ]),
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: content.faqs.map(({ q, a }) => ({
                "@type": "Question",
                name: q,
                acceptedAnswer: { "@type": "Answer", text: a },
              })),
            },
          ]),
        },
      ],
    };
  },
  component: TownPage,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center">
      <Link to="/" className="text-safety">
        Go Home
      </Link>
    </div>
  ),
  errorComponent: () => <div className="p-10">Error.</div>,
});

function TownPage() {
  const { state, town } = Route.useLoaderData();
  const content = buildLocationContent({
    locationName: town,
    stateName: state.name,
    stateCode: state.code,
    scope: "city",
  });
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background sticky top-0 z-40">
        <div className="container-x flex h-16 items-center justify-between">
          <Link to="/" className="font-display text-xl uppercase">
            Vending Machine Free<span className="text-safety">.com</span>
          </Link>
          <Link
            to="/"
            hash="contact"
            className="inline-flex items-center gap-2 rounded-md bg-safety px-4 py-2 text-sm font-semibold text-safety-foreground"
          >
            Apply Free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      <section className="bg-charcoal text-charcoal-foreground py-16">
        <div className="container-x">
          <nav className="text-xs text-white/60 mb-4">
            <Link to="/" className="hover:text-safety">
              Home
            </Link>{" "}
            /{" "}
            <Link to="/coverage" className="hover:text-safety">
              USA Map
            </Link>{" "}
            /{" "}
            <Link to="/states/$state" params={{ state: state.slug }} className="hover:text-safety">
              {state.name}
            </Link>{" "}
            / <span className="text-white">{town}</span>
          </nav>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-safety font-bold">
            <MapPin className="h-4 w-4" /> {town}, {state.code}
          </div>
          <h1 className="mt-3 font-display text-4xl md:text-5xl uppercase">
            Free Vending Machines in {town}, {state.name}
          </h1>
          <p className="mt-4 text-white/80 max-w-2xl text-lg">
            Offices, gyms, warehouses, hospitals and schools across {town} qualify for a FREE
            vending machine — installed, stocked, and serviced by our local {state.name} team at
            zero cost. We try to respond in less than 4hrs – 6hrs.
          </p>
          <a
            href="#apply"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-6 py-3 font-semibold text-safety-foreground"
          >
            {content.cta} <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <GeoSections content={content} locationName={town} stateName={state.name} />

      <AreasSection state={state} city={town} />

      <section className="py-10">
        <div className="container-x text-center flex flex-wrap justify-center gap-5 text-sm">
          <Link
            to="/states/$state"
            params={{ state: state.slug }}
            className="text-steel hover:text-safety font-semibold"
          >
            ← Back to {state.name}
          </Link>
          <Link to="/coverage" className="text-steel hover:text-safety font-semibold">
            USA Map
          </Link>
          <Link to="/" className="text-steel hover:text-safety font-semibold">
            Home
          </Link>
        </div>
      </section>
    </div>
  );
}

function AreasSection({
  state,
  city,
}: {
  state: { slug: string; name: string; code: string };
  city: string;
}) {
  const areas = getAreasForCity(state.slug, slugify(city), city);
  return (
    <section className="py-14 bg-background">
      <div className="container-x">
        <h2 className="font-display text-3xl uppercase">
          Local Towns, Suburbs & Industrial Areas in {city}
        </h2>
        <p className="mt-2 text-muted-foreground">
          Click any area for neighborhood-level coverage, ZIP zones, and FAQs.
        </p>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {areas.map((area) => (
            <Link
              key={area}
              to="/states/$state/$town/$area"
              params={{ state: state.slug, town: slugify(city), area: slugify(area) }}
              title={`Free vending machines in ${area}, ${city}`}
              className="group flex items-center gap-3 rounded-lg border border-border bg-card p-4 hover:border-safety hover:shadow-md transition"
            >
              <div className="h-10 w-10 rounded-md bg-steel/10 text-steel flex items-center justify-center group-hover:bg-safety/15 group-hover:text-safety transition">
                <MapPin className="h-5 w-5" />
              </div>
              <div>
                <div className="font-semibold text-sm">{area}</div>
                <div className="text-xs text-muted-foreground">{state.code} · Free placement</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
